import { Schema } from "prosemirror-model";
import { escapeHtml } from "./utils";
import * as AdvancedMessageEntitiesTypes from "./AdvancedMessageEntitiesTypes.constants";

const entityParseAttrs = (dom, custom = []) => {
  const attrs = {};

  custom.forEach((name) => {
    const dataAttr = `data-${name}`;

    if (dom.hasAttribute(dataAttr)) {
      attrs[name] = escapeHtml(dom.getAttribute(dataAttr));
    }
  });

  attrs.type = escapeHtml(dom.getAttribute("data-type"));
  attrs.text = escapeHtml(dom.textContent);

  return attrs;
};

const entityToDomAttrs = (node, custom = []) => {
  const attrs = {};

  custom.forEach((name) => {
    if (node.attrs[name] !== undefined) {
      attrs[`data-${name}`] = node.attrs[name];
    }
  });

  attrs["data-entity"] = "";
  attrs["data-type"] = node.attrs.type;

  return attrs;
};

const entityToDomText = (node) => node.attrs.text;

const entityDefaultAttrs = (custom) => ({
  ...custom,
  type: {},
  text: {},
});

const mentionNodeSpec = {
  inline: true,
  group: "inline",
  content: "text*",
  marks: "",
  selectable: true,
  draggable: false,
  atom: true,
  attrs: entityDefaultAttrs({
    source: {},
    id: {},
    name: { default: null },
  }),
  parseDOM: [
    {
      tag: `span[data-entity][data-type="${AdvancedMessageEntitiesTypes.MENTION}"][data-source][data-id]`,
      getAttrs: (dom) => entityParseAttrs(dom, ["source", "id", "name"]),
    },
  ],
  toDOM: (node) => [
    "span",
    entityToDomAttrs(node, ["source", "id", "name"]),
    entityToDomText(node),
  ],
};

export const schema = new Schema({
  nodes: {
    doc: {
      content: "block+",
    },
    div: {
      content: "inline*",
      group: "block",
      parseDOM: [{ tag: "div" }],
      toDOM: () => ["div", 0],
    },
    text: {
      group: "inline",
    },
    hard_break: {
      inline: true,
      group: "inline",
      selectable: false,
      parseDOM: [{ tag: "br" }],
      toDOM: () => ["br"],
    },
    [AdvancedMessageEntitiesTypes.MENTION]: mentionNodeSpec,
  },
});

const acceptedEntityTypes = [AdvancedMessageEntitiesTypes.MENTION];

export const toJSON = (data) => {
  const json = {
    type: "doc",
    content: [],
  };
  const message = data?.message?.replace(/\r?\n/g, "\n") || "";
  const entitiesMap =
    data?.entitiesMap?.sort((a, b) => (a.offset < b.offset ? 1 : -1)) || [];
  const entitiesOffsetMap = entitiesMap.reduce(
    (obj, item) => ({ ...obj, [item.offset]: item }),
    {}
  );

  const lines = message.split("\n");
  let linesOffset = 0;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const lineJson = {
      type: "div",
      content: [],
    };
    const characters = line.split("");

    if (index > 0) {
      linesOffset += 1;
    }

    let tmpItem = null;

    for (let charIndex = 0; charIndex < characters.length; charIndex += 1) {
      const char = characters[charIndex];
      const charOffset = linesOffset + charIndex;
      let isEntity = false;

      if (entitiesOffsetMap[charOffset]) {
        const item = entitiesOffsetMap[charOffset];
        const { type, offset, length, ...restAttrs } = item;
        const itemOffset = offset - linesOffset;

        if (acceptedEntityTypes.includes(type)) {
          const text = line.substring(itemOffset, itemOffset + length);

          tmpItem = {
            type,
            attrs: {
              ...restAttrs,
              type,
              text,
            },
          };
          isEntity = true;
          charIndex += item.length - 1;
        }
      }

      if (!isEntity) {
        if (!tmpItem) {
          tmpItem = {
            type: "text",
            text: char,
          };
        } else {
          tmpItem.text += char;
        }
      }

      if (
        tmpItem &&
        (isEntity ||
          characters.length === charIndex + 1 ||
          entitiesOffsetMap[charOffset + 1])
      ) {
        lineJson.content.push(tmpItem);
        tmpItem = null;
      }
    }

    json.content.push(lineJson);
    linesOffset += line.length;
  }

  return json;
};

export const fromJSON = (json) => {
  const data = {
    message: "",
    entitiesMap: [],
  };
  const lines = json?.content || "";
  let offset = 0;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const items = line?.content || [];

    if (index > 0) {
      const value = "\n";

      data.message += value;
      offset += value.length;
    }

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      let value = "";

      if (item.type === "hard_break") {
        value = "\n";
      } else if (item.type === "text") {
        value = item.text;
      } else if (acceptedEntityTypes.includes(item.type)) {
        const { type, key, text: itemText, ...restAttrs } = item.attrs || {};

        value = itemText;

        if (value && type) {
          data.entitiesMap.push({
            ...restAttrs,
            type,
            offset,
            length: value.length,
          });
        }
      }

      data.message += value;
      offset += value.length;
    }
  }

  return data;
};
